import { NavLink } from "react-router-dom";
import { MoreHorizontal, History as HistoryIcon, FileText, MessagesSquare, BookOpen, User2, CalendarDays, Banknote, LineChart, CreditCard, Target, LogOut, Home, PlusCircle, Receipt, Wallet, Download, HelpCircle, Brain, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { tx } from "../../i18n";
import { CurrencySwitcher, LangSwitcher, CountrySwitcher } from "./Switchers";
import { SpaceSwitcher } from "../SpaceSwitcher";
import { useCountry } from "../../contexts/CountryContext";
import { LumfiMark } from "../LumfiLogo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

const primary = (t) => [
  { to: "/dashboard",   label: t("home"),            icon: Home,       testid: "nav-home" },
  { to: "/add",         label: t("nav_add"),         icon: Sparkles, testid: "nav-add" },
  { to: "/receivables", label: t("nav_receivables"), icon: Receipt,    testid: "nav-receivables" },
  { to: "/payments",    label: t("nav_payments"),    icon: Wallet,     testid: "nav-payments" },
];

const wealth = (lang) => [
  { to: "/investments", label: tx(lang, "Investments", "Inversiones", "Investimentos"), icon: LineChart, testid: "nav-investments" },
  { to: "/debts",       label: tx(lang, "Debts", "Deudas", "Dívidas"),                  icon: CreditCard, testid: "nav-debts" },
  { to: "/goals",       label: tx(lang, "Goals", "Metas", "Metas"),                     icon: Target, testid: "nav-goals" },
];

export function MobileTopBar({ t, lang, setLang, user, logout, navigate, loadDemo, currency, setCurrency, fxList, isEs }) {
  const { country, setCountry, countries } = useCountry();
  return (
    <header className="lg:hidden sticky top-0 z-[60] backdrop-blur-xl bg-white/85 border-b border-cream-300 px-3 sm:px-4 h-14 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0 shrink">
        <LumfiMark size={32} />
        <div className="font-serif-display text-xl font-medium text-slate-900 tracking-tight truncate">Lumfi</div>
        {/* iter-170am — Compact space switcher right next to the
            brand so it's visible on every screen on mobile too. */}
        <div className="ml-1">
          <SpaceSwitcher rail />
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {/* On mobile we drop the "Load demo" + bulk-clear buttons to
            free up space for the three core switchers (lang, currency,
            country). Demo data is still reachable from /preferences. */}
        <LangSwitcher testid="lang-switcher-mobile" lang={lang} setLang={setLang} t={t} />
        <CurrencySwitcher testid="currency-switcher-mobile" currency={currency} fxList={fxList} t={t} isEs={isEs} onSelect={setCurrency} />
        <CountrySwitcher testid="country-switcher-mobile" country={country} countries={countries} isEs={isEs} onSelect={setCountry} />
        <Button
          data-testid="logout-btn-mobile"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={async () => { await logout(); navigate("/"); }}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}

export function MobileBottomNav({ t, lang, navigate, user }) {
  const primaryList = primary(t);
  const wealthList = wealth(lang);
  const isAdmin = !!user?.is_admin;
  // iter-169zr · Fase 2 — promote "Add" to a raised FAB in the middle
  // of the bar so the primary action sits in the thumb-zone sweet spot.
  // Order: Home, Receivables, [Add FAB], Payments, More. The FAB is
  // rendered inside its own grid cell so the overall layout stays a
  // 5-col grid (consistent active-state styling for siblings).
  const home = primaryList[0];
  const receivables = primaryList[2];
  const payments = primaryList[3];
  const add = primaryList[1];
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-[60] pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
      <div className="grid grid-cols-5 relative">
        {[home, receivables].map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`${it.testid}-mobile`}
            className={({ isActive }) =>
              cn("flex flex-col items-center justify-center py-2.5 text-[11px] font-medium gap-0.5 min-h-[56px]",
                isActive ? "text-emerald-700" : "text-slate-500"
              )
            }
          >
            <it.icon className="h-5 w-5" />
            {it.label}
          </NavLink>
        ))}
        {/* FAB cell · the icon button is absolutely raised above the bar
            so it floats over the divider line. The cell stays empty so
            grid math doesn't break. */}
        <div className="relative" data-testid="nav-add-fab-cell">
          <NavLink
            to={add.to}
            data-testid={`${add.testid}-mobile`}
            aria-label={add.label}
            className="absolute left-1/2 -translate-x-1/2 -top-5 h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_8px_24px_-6px_rgba(22,163,74,0.55)] inline-flex items-center justify-center transition-all active:scale-95"
          >
            <add.icon className="h-6 w-6" />
          </NavLink>
          {/* Label below the FAB so the active-state pill from siblings
              doesn't visually orphan it. */}
          <span className="absolute inset-x-0 top-9 text-center text-[11px] font-semibold text-emerald-700 pointer-events-none">
            {add.label}
          </span>
        </div>
        {[payments].map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`${it.testid}-mobile`}
            className={({ isActive }) =>
              cn("flex flex-col items-center justify-center py-2.5 text-[11px] font-medium gap-0.5 min-h-[56px]",
                isActive ? "text-emerald-700" : "text-slate-500"
              )
            }
          >
            <it.icon className="h-5 w-5" />
            {it.label}
          </NavLink>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button /* design-system: ALLOW legacy-todo */ data-testid="nav-more-mobile" className="flex flex-col items-center justify-center py-2.5 text-[11px] font-medium gap-0.5 text-slate-500 min-h-[56px] w-full">
              <MoreHorizontal className="h-5 w-5" />
              {tx(lang, "More", "Más", "Mais")}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-56 mb-1">
            <DropdownMenuLabel className="text-xs">{tx(lang, "Menu", "Menú", "Menu")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/history")} data-testid="more-history-mobile">
              <HistoryIcon className="h-4 w-4 mr-2" /> {t("nav_history")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/report")} data-testid="more-report-mobile">
              <FileText className="h-4 w-4 mr-2" /> {t("nav_report")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/recommendations")} data-testid="more-recommendations-mobile">
              <Brain className="h-4 w-4 mr-2" /> {tx(lang, "Recommendations", "Recomendaciones", "Recomendações")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs">{tx(lang, "Wealth", "Patrimonio", "Patrimônio")}</DropdownMenuLabel>
            {wealthList.map((it) => (
              <DropdownMenuItem key={it.to} onClick={() => navigate(it.to)} data-testid={`more-${it.testid.replace(/^nav-/, '')}-mobile`}>
                <it.icon className="h-4 w-4 mr-2" /> {it.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/calendar")} data-testid="more-calendar-mobile">
              <CalendarDays className="h-4 w-4 mr-2" /> {tx(lang, "Calendar", "Calendario", "Calendário")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/accounts")} data-testid="more-accounts-mobile">
              <Banknote className="h-4 w-4 mr-2" /> {tx(lang, "Accounts", "Cuentas", "Contas")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/integrations")} data-testid="more-integrations-mobile">
              <MessagesSquare className="h-4 w-4 mr-2" /> {tx(lang, "Integrations", "Integraciones", "Integrações")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/bots-guide")} data-testid="more-bots-guide-mobile">
              <BookOpen className="h-4 w-4 mr-2" /> {tx(lang, "Bots guide", "Guía de bots", "Guia de bots")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="more-profile-mobile">
              <User2 className="h-4 w-4 mr-2" /> {tx(lang, "My profile", "Mi perfil", "Meu perfil")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/my-plan")} data-testid="more-my-plan-mobile">
              <CreditCard className="h-4 w-4 mr-2" /> {tx(lang, "My plan", "Mi plan", "Meu plano")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="more-pricing-mobile">
              <Sparkles className="h-4 w-4 mr-2" /> {tx(lang, "Plans & packs", "Planes y packs", "Planos e packs")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/help")} data-testid="more-help-mobile">
              <HelpCircle className="h-4 w-4 mr-2" /> {tx(lang, "Help", "Ayuda", "Ajuda")}
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="more-admin-mobile">
                <ShieldCheck className="h-4 w-4 mr-2" /> {tx(lang, "Admin panel", "Panel admin", "Painel admin")}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {/* Fallback path for users whose browser hasn't fired
                beforeinstallprompt yet — the More menu always has the
                install entry visible, taking them to /help which has
                a prominent banner with another install button + iOS
                instructions. */}
            <DropdownMenuItem onClick={() => navigate("/help")} data-testid="more-install-mobile">
              <Download className="h-4 w-4 mr-2" /> {tx(lang, "Install app", "Instalar app", "Instalar app")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
